import React, { useEffect, useRef, useState } from 'react';
import { getData, PostdataWithJson } from '../../API/GlobalApi';
import Notifier from '../../Utils/notify';
import SpinnerLoader from '../../Utils/Spinner';
import '../../assets/css/Modal.css';

const DETAIL_URL = 'students/admit-card-archive/';

// Values are compared case- and whitespace-insensitively: the PDF shouts
// ("AMIT SETH") where the student table is title-cased ("Amit Seth"), and that
// is not a discrepancy worth flagging red.
const norm = (value) => (value ?? '').toString().replace(/\s+/g, ' ').trim().toLowerCase();

const compare = (cardValue, studentValue) => {
    const a = norm(cardValue);
    const b = norm(studentValue);
    if (!a && !b) return 'empty';
    if (!a || !b) return 'single'; // only one side holds this field
    return a === b ? 'match' : 'mismatch';
};

const TONES = {
    match: { bg: '#E6F4EF', color: '#1F6F55', border: '#B7E0D0' },
    mismatch: { bg: '#FDE2E2', color: '#B0281B', border: '#F5B7B1' },
    single: { bg: '#F5F6F8', color: '#4B5563', border: '#E3E6EB' },
    empty: { bg: '#F5F6F8', color: '#9CA3AF', border: '#E3E6EB' },
};

const Cell = ({ value, tone }) => {
    const style = TONES[tone] || TONES.empty;
    return (
        <div
            style={{
                background: style.bg,
                color: style.color,
                border: `1px solid ${style.border}`,
                borderRadius: '6px',
                padding: '7px 10px',
                fontWeight: tone === 'mismatch' ? 600 : 500,
                fontSize: '13px',
                minHeight: '34px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '8px',
                wordBreak: 'break-word',
            }}
        >
            <span>{value || '—'}</span>
            {tone === 'match' && <span aria-hidden="true">✓</span>}
            {tone === 'mismatch' && <span aria-hidden="true">✗</span>}
        </div>
    );
};

const MatchBadge = ({ match }) => {
    let label = 'Not matched';
    let tone = TONES.mismatch;

    if (match?.is_student_matched) {
        label = `Matched — ${match.matching_confidence}%`;
        tone = TONES.match;
    } else if (match?.is_probable_matched) {
        label = `Probable match — ${match.probable_matching_confidence}%`;
        tone = { bg: '#FDF3DC', color: '#8A6100', border: '#F0DCA8' };
    }

    return (
        <span
            style={{
                background: tone.bg,
                color: tone.color,
                border: `1px solid ${tone.border}`,
                borderRadius: '20px',
                padding: '5px 14px',
                fontSize: '13px',
                fontWeight: 600,
                whiteSpace: 'nowrap',
            }}
        >
            {label}
        </span>
    );
};

const SectionHeading = ({ children }) => (
    <div
        style={{
            gridColumn: '1 / -1',
            fontSize: '11px',
            fontWeight: 700,
            letterSpacing: '0.6px',
            textTransform: 'uppercase',
            color: '#7F56DA',
            marginTop: '6px',
        }}
    >
        {children}
    </div>
);

export const AdmitCardCompareModal = ({ isOpen, onClose, admitCardId, onAssigned }) => {
    const [loading, setLoading] = useState(false);
    const [detail, setDetail] = useState(null);

    const [query, setQuery] = useState('');
    const [searching, setSearching] = useState(false);
    const [candidates, setCandidates] = useState(null);
    const [assigningId, setAssigningId] = useState(null);
    const searchTimer = useRef(null);

    const loadDetail = async () => {
        setLoading(true);
        try {
            const response = await getData(`${DETAIL_URL}${admitCardId}/`);
            if (response?.error) {
                Notifier.error(response.error);
                onClose();
                return;
            }
            setDetail(response);
        } catch (error) {
            Notifier.error('Failed to load admit card details');
            onClose();
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!isOpen || !admitCardId) return;
        setDetail(null);
        setQuery('');
        setCandidates(null);
        loadDetail();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, admitCardId]);

    // Debounced so a fast typist doesn't fire a request per keystroke.
    useEffect(() => {
        if (!isOpen) return undefined;
        if (searchTimer.current) clearTimeout(searchTimer.current);
        const q = query.trim();
        if (q.length < 2) {
            setCandidates(null);
            return undefined;
        }
        searchTimer.current = setTimeout(async () => {
            setSearching(true);
            try {
                const response = await getData(
                    `${DETAIL_URL}${admitCardId}/student-search/?q=${encodeURIComponent(q)}`
                );
                setCandidates(response?.students || []);
            } catch (error) {
                setCandidates([]);
            } finally {
                setSearching(false);
            }
        }, 350);
        return () => clearTimeout(searchTimer.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query, isOpen, admitCardId]);

    const assign = async (studentId) => {
        setAssigningId(studentId ?? 'clear');
        try {
            const response = await PostdataWithJson(`${DETAIL_URL}${admitCardId}/assign-student/`, {
                student_id: studentId,
            });
            if (response?.error) {
                Notifier.error(response.error);
                return;
            }
            Notifier.success(response.message);
            setQuery('');
            setCandidates(null);
            await loadDetail();
            onAssigned?.();
        } catch (error) {
            Notifier.error('Could not assign student');
        } finally {
            setAssigningId(null);
        }
    };

    if (!isOpen) return null;

    const card = detail?.card_side;
    const student = detail?.student_side;

    // Fields the PDF and the student table both carry — these can genuinely agree
    // or disagree, so they get the green/red treatment.
    const comparableRows = card
        ? [
              { label: 'Name', card: card.candidate_name, student: student?.full_name },
              { label: "Father's Name", card: card.father_name, student: student?.father_name },
              { label: "Mother's Name", card: card.mother_name, student: student?.mother_name },
              { label: 'Gender', card: card.gender, student: student?.gender },
              { label: 'Roll Number', card: card.roll_number, student: student?.roll_number },
              { label: 'Course', card: card.course, student: student?.course },
              { label: 'Specialization', card: card.specialization, student: student?.specialization },
              { label: 'Semester', card: card.academic_group, student: student?.academic_group },
          ]
        : [];

    // Fields only one side has. Never red — an absence here is normal, not a conflict.
    const singleSidedRows = card
        ? [
              { label: 'Enrollment No.', card: card.enroll_number, student: null },
              { label: 'Exam Year', card: card.examination_year, student: null },
              { label: 'Admit Card Type', card: card.card_type_display, student: null },
              { label: 'Registration No.', card: null, student: student?.permanent_registration_number },
              { label: 'Section', card: null, student: student?.lag },
              { label: 'Mobile', card: null, student: student?.mobile },
          ]
        : [];

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div
                className="modal-content"
                onClick={(e) => e.stopPropagation()}
                style={{ position: 'relative', width: '70%', maxWidth: '900px', maxHeight: '90vh', overflowY: 'auto', padding: '28px' }}
            >
                {/* Header */}
                <div
                    className="modal-header-content"
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}
                >
                    <h2 className="text-primary" style={{ margin: 0 }}>Admit Card vs Student Record</h2>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                        {detail && <MatchBadge match={detail.match} />}
                        <button className="close-btn" onClick={onClose}>
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <path d="M1 1L13 13M13 1L1 13" stroke="#333" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                        </button>
                    </div>
                </div>

                {loading || !detail ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
                        <SpinnerLoader />
                    </div>
                ) : (
                    <>
                        {student?.is_probable && (
                            <div
                                style={{
                                    background: '#FDF3DC',
                                    border: '1px solid #F0DCA8',
                                    color: '#8A6100',
                                    borderRadius: '6px',
                                    padding: '8px 12px',
                                    fontSize: '13px',
                                    margin: '12px 0 4px',
                                }}
                            >
                                This student is only a <strong>probable</strong> match — not confirmed.
                            </div>
                        )}

                        {!student && (
                            <div
                                style={{
                                    background: '#FDE2E2',
                                    border: '1px solid #F5B7B1',
                                    color: '#B0281B',
                                    borderRadius: '6px',
                                    padding: '8px 12px',
                                    fontSize: '13px',
                                    margin: '12px 0 4px',
                                }}
                            >
                                No student matched this admit card. Only the card side is shown.
                            </div>
                        )}

                        {/* Manual assignment */}
                        <div
                            style={{
                                marginTop: '16px',
                                padding: '12px 14px',
                                border: '1px solid #E3E6EB',
                                borderRadius: '8px',
                                background: '#FCFCFD',
                            }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                                <label style={{ fontSize: '12px', fontWeight: 700, letterSpacing: '0.5px', textTransform: 'uppercase', color: '#7F56DA' }}>
                                    Assign a student manually
                                </label>
                                {student && (
                                    <button
                                        onClick={() => assign(null)}
                                        disabled={assigningId !== null}
                                        style={{
                                            border: '1px solid #F5B7B1', background: 'white', color: '#B0281B',
                                            borderRadius: '6px', padding: '4px 10px', fontSize: '12px',
                                            fontWeight: 600, cursor: assigningId ? 'not-allowed' : 'pointer',
                                        }}
                                    >
                                        {assigningId === 'clear' ? 'Clearing…' : 'Clear assignment'}
                                    </button>
                                )}
                            </div>

                            <input
                                type="text"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Search this course by name, registration no. or mobile…"
                                style={{
                                    width: '100%', height: '36px', padding: '0 10px',
                                    border: '1px solid #D8DBE0', borderRadius: '6px', fontSize: '13px', outline: 'none',
                                }}
                            />

                            {searching && (
                                <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '8px' }}>Searching…</div>
                            )}

                            {!searching && candidates && candidates.length === 0 && (
                                <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '8px' }}>
                                    No students on this course match “{query}”.
                                </div>
                            )}

                            {!searching && candidates && candidates.length > 0 && (
                                <div style={{ marginTop: '8px', maxHeight: '190px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    {candidates.map((c) => (
                                        <div
                                            key={c.student_id}
                                            style={{
                                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                                gap: '10px', padding: '7px 10px', borderRadius: '6px',
                                                border: '1px solid #E3E6EB',
                                                background: c.is_current ? '#E6F4EF' : 'white',
                                            }}
                                        >
                                            <div style={{ minWidth: 0 }}>
                                                <div style={{ fontSize: '13px', fontWeight: 600, color: '#222F3E' }}>
                                                    {c.full_name}
                                                    {c.is_current && (
                                                        <span style={{ color: '#1F6F55', fontWeight: 600 }}> — currently assigned</span>
                                                    )}
                                                </div>
                                                <div style={{ fontSize: '12px', color: '#6B7280' }}>
                                                    {[c.permanent_registration_number, c.mobile, c.academic_group, c.lag]
                                                        .filter(Boolean)
                                                        .join('  ·  ')}
                                                </div>
                                                <div style={{ fontSize: '12px', color: '#9CA3AF' }}>
                                                    {[c.father_name && `F: ${c.father_name}`, c.mother_name && `M: ${c.mother_name}`, c.gender]
                                                        .filter(Boolean)
                                                        .join('  ·  ')}
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => assign(c.student_id)}
                                                disabled={c.is_current || c.already_assigned || assigningId !== null}
                                                title={
                                                    c.already_assigned
                                                        ? 'Already assigned to another admit card for this course, semester and type'
                                                        : ''
                                                }
                                                style={{
                                                    flexShrink: 0, border: 'none', borderRadius: '6px',
                                                    padding: '6px 14px', fontSize: '12px', fontWeight: 600, color: '#fff',
                                                    background:
                                                        c.is_current || c.already_assigned || assigningId !== null ? '#C7C9D1' : '#7F56DA',
                                                    cursor:
                                                        c.is_current || c.already_assigned || assigningId !== null ? 'not-allowed' : 'pointer',
                                                }}
                                            >
                                                {assigningId === c.student_id
                                                    ? 'Assigning…'
                                                    : c.already_assigned
                                                    ? 'Taken'
                                                    : 'Assign'}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Column headings */}
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '150px 1fr 1fr',
                                gap: '8px 12px',
                                alignItems: 'center',
                                marginTop: '16px',
                            }}
                        >
                            <div />
                            <div style={{ fontWeight: 700, fontSize: '13px', color: '#222F3E', paddingBottom: '2px' }}>
                                On Admit Card / Selected at Upload
                            </div>
                            <div style={{ fontWeight: 700, fontSize: '13px', color: '#222F3E', paddingBottom: '2px' }}>
                                In Student Record
                            </div>

                            <SectionHeading>Compared</SectionHeading>
                            {comparableRows.map((row) => {
                                const tone = compare(row.card, row.student);
                                return (
                                    <React.Fragment key={row.label}>
                                        <div style={{ fontSize: '13px', color: '#6B7280', fontWeight: 500 }}>{row.label}</div>
                                        <Cell value={row.card} tone={tone} />
                                        <Cell value={row.student} tone={tone} />
                                    </React.Fragment>
                                );
                            })}

                            <SectionHeading>Recorded on one side only</SectionHeading>
                            {singleSidedRows.map((row) => (
                                <React.Fragment key={row.label}>
                                    <div style={{ fontSize: '13px', color: '#6B7280', fontWeight: 500 }}>{row.label}</div>
                                    <Cell value={row.card} tone={row.card ? 'single' : 'empty'} />
                                    <Cell value={row.student} tone={row.student ? 'single' : 'empty'} />
                                </React.Fragment>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default AdmitCardCompareModal;
